import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Download, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { SECTION_MODULES } from '@/lib/sectionModules';
import { getModuleDocuments, downloadCsv } from '@/services/sectionReportsApi';
import { ReportHeader, DateSearchFilters, Pager, UnknownSection } from '@/components/sectionReports/ReportParts';

const PAGE_SIZE = 20;
const EMPTY = { dateFrom: '', dateTo: '', search: '' };
const toParams = (f) => ({ date_from: f.dateFrom || undefined, date_to: f.dateTo || undefined, search: f.search.trim() || undefined });

const DocRow = ({ r, idx, showPieces }) => (
  <TableRow data-testid={`docs-row-${idx}`}>
    <TableCell className="text-gray-700 whitespace-nowrap">{r.date || '—'}</TableCell>
    <TableCell className="font-mono font-medium text-gray-900" data-testid={`docs-doc-${idx}`}>{r.document_no}</TableCell>
    <TableCell>
      <Badge variant="outline" className={r.type === 'Gate Entry' ? 'bg-sky-50 text-sky-700' : 'bg-violet-50 text-violet-700'}>{r.type}</Badge>
    </TableCell>
    <TableCell className="text-gray-700">{r.party || '—'}</TableCell>
    <TableCell className="text-gray-600">{r.vehicle_no || '—'}</TableCell>
    <TableCell className="text-right text-gray-700 tabular-nums" data-testid={`docs-out-${idx}`}>{r.out_bundles}/{r.total_bundles}</TableCell>
    {showPieces && (
      <TableCell className="text-right text-gray-600 tabular-nums">{r.pieces_total != null ? `${r.pieces_out}/${r.pieces_total}` : '—'}</TableCell>
    )}
    <TableCell>
      <Badge className={r.status === 'Completed' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-amber-100 text-amber-700 hover:bg-amber-100'}
        data-testid={`docs-status-${idx}`}>{r.status}</Badge>
    </TableCell>
  </TableRow>
);

const SectionDocumentsReport = ({ sectionId: propSectionId }) => {
  const params = useParams();
  const sectionId = propSectionId || params.sectionId;
  const mod = SECTION_MODULES[sectionId];
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState({ rows: [], total: 0, total_pages: 1, page: 1 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (page = 1, f = filters) => {
    if (!mod) return;
    setLoading(true);
    try {
      setData(await getModuleDocuments(mod.key, { page, page_size: PAGE_SIZE, ...toParams(f) }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load documents report');
    } finally {
      setLoading(false);
    }
  }, [mod, filters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1); }, [sectionId]);

  if (!mod) return <UnknownSection sectionId={sectionId} />;

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadCsv(`/section-reports/${mod.key}/documents/export`, toParams(filters),
        `${mod.key}_documents_report_${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success('CSV exported');
    } catch (err) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const rows = data.rows || [];
  const showPieces = rows.some((r) => r.pieces_total != null);
  const heads = ['Date', 'Document No', 'Type', 'From / Vendor', 'Vehicle No', 'Bundles Out', ...(showPieces ? ['Pieces Out'] : []), 'Status'];
  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="section-documents-report">
      <ReportHeader icon={FileText} title="Documents Report"
        subtitle={`${mod.name} · Completed once every bundle is internal-transferred out or shipped outward`}
        backRoute={`/sections/${sectionId}`}
        actions={(
          <Button onClick={exportCsv} disabled={exporting || loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="docs-export-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Export CSV
          </Button>
        )} />
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <DateSearchFilters filters={filters} loading={loading} placeholder="Document no, vendor, vehicle, vendor doc no, transfer id…"
          onChange={(p) => setFilters((f) => ({ ...f, ...p }))} onSearch={() => load(1)}
          onClear={() => { setFilters(EMPTY); load(1, EMPTY); }} />
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table data-testid="docs-table">
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    {heads.map((h) => <TableHead key={h} className={`font-semibold ${h.includes('Out') ? 'text-right' : ''}`}>{h}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={heads.length} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></TableCell></TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={heads.length} className="text-center py-12 text-gray-500" data-testid="docs-empty">No documents found.</TableCell></TableRow>
                  ) : rows.map((r, idx) => <DocRow key={`${r.type}-${r.document_no}`} r={r} idx={idx} showPieces={showPieces} />)}
                </TableBody>
              </Table>
            </div>
            <Pager page={data.page || 1} pageSize={PAGE_SIZE} total={data.total || 0} totalPages={data.total_pages || 1}
              loading={loading} onPage={(p) => load(p)} noun="documents" />
          </CardContent>
        </Card>
        {showPieces && (
          <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Pieces Out = tagged pieces already packed into bundles that left the section.
          </p>
        )}
      </main>
    </div>
  );
};

export default SectionDocumentsReport;
